// The `DiscoverSection` component presents a slider featuring various services or products, each displayed as a
// tile with images and descriptions. It adapts to different screen sizes using hooks like `useTouchDevice` and `
// useMediaQuery` for a responsive, user-friendly experience. Its main purpose is to offer an interactive and
// visually engaging way for users to explore additional content through a carousel interface.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, mediumDesktop, tablet } from 'helpers/utils/screensizes';
import Wrapper from 'components/HOC/wrapper';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { Carousel, CarouselContentItem } from 'types/next';
import DiscoverTile from './tile';
import useLocale from 'helpers/hooks/useLocale';

export type DiscoverData = {
  content: Carousel | undefined;
};

const DiscoverSection = ({ content }: DiscoverData) => {
  const { isTouchDevice } = useTouchDevice();
  const contentItems = (content?.contentItems as CarouselContentItem[]) || [];
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [startIndex, setStartIndex] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);

  const [isMediumDesktopSize] = useMediaQuery(mediumDesktop);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isTablet] = useMediaQuery(tablet);
  const [isScrolledToStart, setIsScrolledToStart] = useState(true);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const { getCurrency } = useLocale();
  const selectedLocale = getCurrency() === 'GBP' ? 'en-GB' : 'en-EU';

  const slidesPerView = useMemo(() => {
    if (isMediumDesktopSize) return content?.slidesPerView?.largeDesktop || 4.5;
    if (isDesktopSize) return content?.slidesPerView?.desktop || 3.5;
    if (isTablet) return content?.slidesPerView?.tablet || 2.5;
    return content?.slidesPerView?.mobile || 1.5;
  }, [isMediumDesktopSize, isDesktopSize, isTablet]);

  // Update slide width on mount & when container size changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      setSlideWidth(scrollContainerRef.current.offsetWidth / slidesPerView);
    }
  }, [slidesPerView]);

  //  Scroll event handler to track scrollbar position
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;

    const isAtStart = scrollLeft <= 5; // Small buffer to avoid floating point errors
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5;

    setIsScrolledToStart(isAtStart);
    setIsScrolledToEnd(isAtEnd);

    //  Ensure startIndex updates correctly to re-enable left button
    const newStartIndex = Math.round(scrollLeft / slideWidth);
    setStartIndex(newStartIndex);
  };

  // Scroll Left with Smooth Behavior
  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -slideWidth,
        behavior: 'smooth', // Enable smooth scrolling
      });
    }
  };

  // Scroll Right with Smooth Behavior
  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: slideWidth,
        behavior: 'smooth', // Enable smooth scrolling
      });
    }
  };

  //  Attach scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll(); // Run initially to set correct button states
    }
    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, [contentItems.length, slidesPerView, slideWidth]);

  return (
    <Wrapper clearDefaultStyles className="homepage-carousels-section pb-16 pl-16">
      <div className={`mb-16 flex items-center ${isDesktopSize ? 'relative justify-center' : 'justify-between pr-10'}`}>
        <div className="align-center flex justify-center text-center">
          <h2 className="font-body text-24 font-bold uppercase leading-9 sm:text-36">
            {content?.heading?.[selectedLocale] ?? ''}
          </h2>
        </div>

        <div className={`flex items-center justify-end gap-x-6 ${isDesktopSize ? 'absolute right-[1rem]' : ''}`}>
          <button
            className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-[#e0e0e0] p-3 text-gray-700 hover:bg-[#c0c0c0] 
    ${isScrolledToStart ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={handleScrollLeft}
            disabled={isScrolledToStart}
            aria-disabled={isScrolledToStart}
          >
            <ChevronLeftIcon
              className="mt-2 h-20 w-20 font-bold text-[#404040]"
              stroke="currentColor"
              strokeWidth="3"
              fill="none"
            />
          </button>
          <button
            className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-[#e0e0e0] p-3 text-gray-700 hover:bg-[#c0c0c0] 
    ${isScrolledToEnd ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={handleScrollRight}
            disabled={isScrolledToEnd}
            aria-disabled={isScrolledToEnd}
          >
            <ChevronRightIcon
              className="mt-2 h-20 w-20 font-bold text-[#404040]"
              stroke="currentColor"
              strokeWidth="3"
              fill="none"
            />
          </button>
        </div>
      </div>
      <div
        ref={scrollContainerRef}
        className="ag__cc__wrapper flex"
        style={{
          scrollSnapType: 'x mandatory',
          gap: '16px',
          WebkitOverflowScrolling: 'touch',
          overflowY: 'hidden',
          paddingBottom: '10px',
        }}
      >
        {contentItems?.map((data, index) => (
          <div
            key={index}
            className="w-[calc(100% / 5)] flex h-full flex-col items-stretch"
            style={{
              flex: `0 0 calc(100% / ${slidesPerView})`,
              scrollSnapAlign: 'start',
            }}
          >
            <DiscoverTile data={data} locale={selectedLocale} />
          </div>
        ))}
      </div>
    </Wrapper>
  );
};

export default DiscoverSection;
